import { useMemo, useState } from 'react';

import { BookMarkedIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { Button } from '@/components/ui/button';
import { useBrainId } from '@/hooks/useBrainId';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import { getSearchStrategy } from '@/hooks/useEffectiveTools';
import { SearchStrategyMode } from '@/shared/types/brain';

import { KnowledgeSettingsDialog } from './KnowledgeSettingsDialog';

export function KnowledgeBaseSelector() {
  const { t } = useTranslation();
  const brainId = useBrainId();
  const { brain, updateBrainAsync, patchBrainGuidelinesAsync } = useBrains(brainId);
  const { collection: selectedKnowledgeBase, collectionsByLanguage } = useCollections(
    brain?.collection_id
  );
  const [isOpen, setIsOpen] = useState(false);

  const knowledgeBases = useMemo(
    () => (brain?.language ? (collectionsByLanguage(brain.language) ?? []) : []),
    [brain?.language, collectionsByLanguage]
  );

  const modeLabels: Record<SearchStrategyMode, string> = {
    [SearchStrategyMode.PREFETCH]: t('ai_agents.knowledge_search.prefetch_short'),
    [SearchStrategyMode.ON_DEMAND]: t('ai_agents.knowledge_search.on_demand_short'),
  };

  return (
    <>
      <Button
        type="button"
        variant="link-secondary"
        aria-label={t('collections.collection')}
        onClick={() => setIsOpen(true)}
        className="m-0 h-auto justify-start p-0 text-sm leading-6 font-medium text-foreground"
      >
        <span className="flex max-w-60 min-w-0 items-center justify-start gap-2">
          {selectedKnowledgeBase ? (
            <>
              <BookMarkedIcon className="size-4 shrink-0 text-foreground-muted" />
              <TruncatedText text={selectedKnowledgeBase.name} className="max-w-[136px]" />
              <span className="shrink-0 text-foreground-muted">
                · {modeLabels[getSearchStrategy(brain).mode]}
              </span>
            </>
          ) : (
            <span>{t('common.connect')}</span>
          )}
        </span>
      </Button>

      {isOpen && brain && (
        <KnowledgeSettingsDialog
          brain={brain}
          knowledgeBases={knowledgeBases}
          updateBrainAsync={updateBrainAsync}
          patchBrainGuidelinesAsync={patchBrainGuidelinesAsync}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
